// Reusable TaxTopicPage component for /tax/$topic (TH + EN).
import { Link } from "@tanstack/react-router";
import type { TaxTopic } from "@/data/tax-topics";
import { TrustLayer } from "@/components/TrustLayer";
import { Sparkles, AlertTriangle, FileText, Wallet, ShieldCheck, Phone, MessageCircle, BookOpen, ChevronRight } from "lucide-react";

const L = {
  th: {
    quick: "AI Quick Answer", who: "เหมาะกับใคร", rules: "หลักเกณฑ์สำคัญ", examples: "ตัวอย่างคำนวณ", pitfalls: "ข้อควรระวัง", faq: "คำถามที่พบบ่อย", refs: "อ้างอิงทางการ", related: "หัวข้อที่เกี่ยวข้อง", ctaCall: "โทร 092-242-9293", ctaLine: "LINE @nyc-notary", consult: "ปรึกษาภาษีกับ NYC Legal & Notary — บริการยื่นภาษี ภ.ง.ด.90/91 และวางแผนภาษีต่างประเทศโดยทีมที่ปรึกษาด้านกฎหมายและภาษีของ NYC", back: "← กลับหน้ารวม",
  },
  en: {
    quick: "AI Quick Answer", who: "Who this is for", rules: "Key Rules", examples: "Worked Examples", pitfalls: "Common Pitfalls", faq: "FAQ", refs: "Official References", related: "Related Topics", ctaCall: "Call +66 92-242-9293", ctaLine: "LINE @nyc-notary", consult: "Talk to NYC Legal & Notary — PND.90/91 filing and foreign-income tax planning by NYC's licensed advisory team.", back: "← Back to all topics",
  },
};

export interface TaxTopicPageProps {
  topic: TaxTopic;
  locale: "th" | "en";
  hubPath: string; // /tax or /en/tax
  relatedBaseTax: string; // base for related topic links
}

export function TaxTopicPage({ topic, locale, hubPath, relatedBaseTax }: TaxTopicPageProps) {
  const t = L[locale];
  const title = locale === "th" ? topic.titleTh : topic.titleEn;
  const tagline = locale === "th" ? topic.taglineTh : topic.taglineEn;
  const quick = locale === "th" ? topic.quickAnswerTh : topic.quickAnswerEn;
  const facts = locale === "th" ? topic.keyFactsTh : topic.keyFactsEn;
  const who = locale === "th" ? topic.whoForTh : topic.whoForEn;
  const rules = locale === "th" ? topic.rulesTh : topic.rulesEn;
  const examples = locale === "th" ? topic.examplesTh : topic.examplesEn;
  const pitfalls = locale === "th" ? topic.pitfallsTh : topic.pitfallsEn;
  const faqs = locale === "th" ? topic.faqsTh : topic.faqsEn;
  const cites = locale === "th" ? topic.citationsTh : topic.citationsEn;

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* Breadcrumb */}
      <nav className="mx-auto max-w-5xl px-4 pt-6 text-xs text-muted-foreground" aria-label="Breadcrumb">
        <Link to={hubPath as "/tax"} className="hover:underline">{locale === "th" ? "ภาษีไทย" : "Thai Tax"}</Link>
        <ChevronRight className="mx-1 inline h-3 w-3" />
        <span>{title}</span>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-8 pb-12">
        <div className="mx-auto max-w-5xl">
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary">
            {locale === "th" ? "เนื้อหาเชิงลึก — ปรับปรุง 2026" : "Deep-Dive Guide — Updated 2026"}
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">{title}</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">{tagline}</p>

          {/* Quick Answer */}
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true" itemScope itemType="https://schema.org/Question">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" aria-hidden="true" /> {t.quick}
            </div>
            <h2 className="text-base font-bold sm:text-lg" itemProp="name">{title}</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-sm leading-relaxed text-foreground sm:text-base" itemProp="text">{quick}</p>
            </div>
          </div>

          {/* Key Facts */}
          <dl className="mt-6 grid max-w-3xl grid-cols-1 gap-0 overflow-hidden rounded-2xl border border-border bg-card text-sm sm:grid-cols-2" data-speakable="true">
            {facts.map((r, i) => (
              <div key={r.k} className={`flex justify-between gap-3 px-4 py-2.5 ${i < facts.length - 1 ? "border-b border-border" : ""}`}>
                <dt className="font-medium text-muted-foreground">{r.k}</dt>
                <dd className="text-right font-semibold">{r.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> {t.ctaCall}
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> {t.ctaLine}
            </a>
          </div>
        </div>
      </section>

      {/* Who for */}
      <section className="px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold">{t.who}</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {who.map((w) => (
              <li key={w} className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Rules */}
      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">{t.rules}</h2>
          <ol className="mt-6 space-y-4">
            {rules.map((s, i) => (
              <li key={s.name} className="flex gap-4 rounded-2xl border border-border bg-card p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground">{i + 1}</div>
                <div>
                  <h3 className="font-bold">{s.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Examples */}
      {examples.length > 0 && (
        <section className="px-4 py-12">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-extrabold sm:text-3xl">{t.examples}</h2>
            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              {examples.map((ex) => (
                <div key={ex.title} className="rounded-2xl border border-border bg-card p-5">
                  <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-primary">
                    <Wallet className="h-3 w-3" /> {ex.title}
                  </div>
                  <p className="text-sm text-muted-foreground">{ex.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pitfalls */}
      <section className="bg-amber-500/5 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="flex items-center gap-2 text-2xl font-extrabold sm:text-3xl">
            <AlertTriangle className="h-6 w-6 text-amber-600" /> {t.pitfalls}
          </h2>
          <ul className="mt-4 grid gap-2">
            {pitfalls.map((p) => (
              <li key={p} className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-card p-3 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 py-12">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">{t.faq}</h2>
          <div className="mt-6 space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card p-4">
                <summary className="cursor-pointer font-bold">{f.q}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Citations */}
      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="flex items-center gap-2 text-xl font-bold"><BookOpen className="h-5 w-5" /> {t.refs}</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {cites.map((c) => (
              <li key={c.url}>
                <a href={c.url} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-2 rounded-lg border border-border bg-card p-3 text-sm hover:border-primary">
                  <FileText className="h-4 w-4 text-primary" />
                  <span className="font-medium">{c.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Related */}
      <section className="px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-bold">{t.related}</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {topic.relatedSlugs.map((s) => (
              <a key={s} href={`${relatedBaseTax}/${s}`} className="rounded-xl border border-border bg-card p-4 text-sm hover:border-primary">
                <ChevronRight className="mr-1 inline h-3 w-3" />{s.replace(/-/g, " ")}
              </a>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-primary/30 bg-primary/5 p-5 text-sm">{t.consult}</div>
          <Link to={hubPath as "/tax"} className="mt-6 inline-block text-sm text-primary hover:underline">{t.back}</Link>
        </div>
      </section>

      <TrustLayer />
    </main>
  );
}
